import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TbPlus } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Button from '../../components/buttons/Button';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';
import { getEventTicketStats } from '../../../services/financial/tickets.service';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { PaginatedEventTicketStats, EventTicketStatsDto } from '../../../models/marketplace.model';
import SearchBar from '../../components/forms/SearchBar';

export default function EventTicketsPage(): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.eventTickets' });
  const selectedDashboard = useSelector((state: any) => state.application.selectedDashboard);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<EventTicketStatsDto[]>([]);
  const [meta, setMeta] = useState<{ total: number; page: number; perPage: number }>({ total: 0, page: 1, perPage: 20 });
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredStats = stats.filter((s) => !searchQuery || (s.title || '').toLowerCase().includes(searchQuery.toLowerCase()));

  const loadData = async (page = 1) => {
    if (!selectedDashboard) return;
    try {
      setLoading(true);
      const res: PaginatedEventTicketStats = await getEventTicketStats(selectedDashboard, page, meta.perPage);
      setStats(res.items);
      setMeta(res.meta);
    } catch (error) {
      console.error('Error loading event ticket stats', error);
      toast.error(t('toast.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!selectedDashboard) return;
      try {
        setLoading(true);
        const res: PaginatedEventTicketStats = await getEventTicketStats(selectedDashboard, 1, meta.perPage);
        if (!mounted) return;
        setStats(res.items);
        setMeta(res.meta);
      } catch (error) {
        console.error('Error loading event ticket stats', error);
        toast.error(t('toast.failedToLoad'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [selectedDashboard, meta.perPage]);

  const handlePageChange = (newPage: number) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
    loadData(newPage);
  };

  if (!stats.length && !loading) {
    return (
      <div className="flex flex-col gap-8">
        <EmptyScreenView
          title={t('noTickets')}
          subtitle={t('createFirstTicket')}
          primaryButton={<PrimaryButton text={t('newEvent')} icon={TbPlus} onClick={() => navigate('/events/create')} />}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div>
      <div className="mb-4 flex items-center justify-between">
        {stats.length > 0 && (
          <>
            <SearchBar
              className="max-w-[380px]"
              search={searchQuery}
              setSearch={setSearchQuery}
            />
            <PrimaryButton
              text={t('newEvent')}
              icon={TbPlus}
              onClick={() => navigate('/events/create')}
            />
          </>
        )}
      </div>
        <div className="relative w-full overflow-x-auto">
          <BaseTable>
            <BaseTableHeader>
                <BaseTableHeaderRow>
                  <HeaderCell>{t('table.event')}</HeaderCell>
                  <HeaderCell>{t('table.date')}</HeaderCell>
                  <HeaderCell>{t('table.ticketsSold')}</HeaderCell>
                  <HeaderCell>{t('table.revenue')}</HeaderCell>
              </BaseTableHeaderRow>
            </BaseTableHeader>
            <BaseTableBody>
              <BaseTableSpacerRow />
              {filteredStats.map((row) => (
                  <BaseTableDataRow key={row.eventId} className="group cursor-pointer hover:bg-secondary-200/50 transition-all" onClick={() => navigate('/events/details', { state: { eventId: row.eventId, origin: 'eventTickets' } })}>
                    <DataCell>
                      <span className="font-medium text-primary-900">{row.title}</span>
                    </DataCell>
                    <DataCell>
                      {row.startTime ? new Date(row.startTime).toLocaleDateString(i18n.language) : '-'}
                    </DataCell>
                    <DataCell>
                      <span className="font-medium text-primary-900">{row.ticketsSold}</span>
                    </DataCell>
                    <DataCell>
                      <span className="font-medium text-primary-900">{formatEuroCentsWithSign(row.revenue, i18n.language)}</span>
                    </DataCell>
                  </BaseTableDataRow>
              ))}

              <BaseTableDataRow key="totals_row" className="group bg-secondary-200/50">
                <DataCell className="py-2">{t('table.totals')}</DataCell>
                <DataCell className="py-2" />
                <DataCell className="py-2">{filteredStats.reduce((acc, r) => acc + r.ticketsSold, 0)}</DataCell>
                <DataCell className="py-2">{formatEuroCentsWithSign(filteredStats.reduce((acc, r) => acc + r.revenue, 0), i18n.language)}</DataCell>
              </BaseTableDataRow>
            </BaseTableBody>
          </BaseTable>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div>
            <p className="text-sm text-gray-600">
              {t('pagination.pageInfo', { current: meta.page, total: Math.ceil(meta.total / meta.perPage) })}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="px-3 py-1 disabled:opacity-50"
              onClick={() => handlePageChange(Math.max(1, meta.page - 1))}
              disabled={meta.page <= 1 || loading}
            >
              {t('pagination.prev')}
            </Button>
            <Button
              variant="secondary"
              className="px-3 py-1 disabled:opacity-50"
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page * meta.perPage >= meta.total || loading}
            >
              {t('pagination.next')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 text-primary-900', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge(
        'whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900',
        className,
      )}
      {...props}
    />
  );
}
